import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./RestriccionMateria.css";
import { message } from "../../Mensaje/Mensaje";

export default function RestriccionMateria({ DietasController, onAtras, onSiguiente }) {
  const [items, setItems] = useState([]);
  const [restr, setRestr] = useState({});

  // ---- Utilidades numéricas ----
  const numOr = useCallback((v, d) => {
    if (v === "" || v === null || v === undefined) return d;
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : d;
  }, []);

  const toNum = useCallback((v) => {
    if (v === "" || v === null || v === undefined) return NaN;
    const n = Number(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : NaN;
  }, []);

  const pickNum = useCallback((...vars) => {
    for (const v of vars) {
      const n = toNum(v);
      if (Number.isFinite(n)) return n;
    }
    return undefined;
  }, [toNum]);

  // ---- Carga inicial ----
  useEffect(() => {
    const seleccionadas =
      DietasController?.getSeleccionadas?.() ||
      JSON.parse(localStorage.getItem("np_sel") || "[]");
    setItems(Array.isArray(seleccionadas) ? seleccionadas : []);

    const prevCtl = DietasController?.getRestricciones?.() || {};
    let prevLS = {};
    try {
      const raw = JSON.parse(localStorage.getItem("np_restriccionMateria") || "{}");
      prevLS = raw.byId || JSON.parse(localStorage.getItem("np_restr") || "{}") || {};
    } catch { }

    const normalize = (src) => {
      const out = {};
      Object.entries(src || {}).forEach(([id, r]) => {
        const costo = numOr(r?.costo, 0);
        const minKg = pickNum(r?.minKg, r?.min_kg, r?.minAsFed, r?.min);
        const maxKg = pickNum(r?.maxKg, r?.max_kg, r?.maxAsFed, r?.max);
        out[id] = {
          costo: Number.isFinite(costo) && costo >= 0 ? costo : 0,
          minKg: Number.isFinite(minKg) && minKg >= 0 ? minKg : "",
          maxKg: Number.isFinite(maxKg) && maxKg >= 0 ? maxKg : "",
        };
      });
      return out;
    };

    const merged = { ...normalize(prevLS), ...normalize(prevCtl) };
    setRestr(merged);
  }, [DietasController, numOr, pickNum]);

  // ---- Filas ----
  const rows = useMemo(() => {
    return (items || []).map((m) => {
      const r = restr?.[m.id] || {};
      return {
        id: m.id,
        nombre: m.nombre || "",
        temporal: !!(m.temporal || m.isTemporal || m.esTemporal),
        costo: r.costo ?? "",
        minKg: r.minKg ?? "",
        maxKg: r.maxKg ?? "",
      };
    });
  }, [items, restr]);

  const setField = (id, field, value) => {
    setRestr((prev) => ({
      ...prev,
      [id]: {
        ...(prev?.[id] || {}),
        [field]: value,
      },
    }));
  };

  // ---- Validación ----
  const validar = useCallback(() => {
    if (!rows.length) {
      message.alert({
        title: "Selecciona materias",
        text: "Debes haber confirmado al menos una materia prima en el paso anterior.",
        buttonText: "Entendido",
      });
      return false;
    }

    for (const r of rows) {
      const costoN = toNum(r.costo);
      if (r.costo === "" || !Number.isFinite(costoN) || costoN < 0) {
        message.alert({
          title: "Costo inválido",
          text: `Ingresa un costo válido (≥ 0) para "${r.nombre}".`,
          buttonText: "Corregir",
        });
        return false;
      }

      const mn = r.minKg !== "" ? toNum(r.minKg) : undefined;
      const mx = r.maxKg !== "" ? toNum(r.maxKg) : undefined;

      if (r.minKg !== "" && (!Number.isFinite(mn) || mn < 0)) {
        message.alert({
          title: "Mínimo inválido",
          text: `Revisa el mínimo (kg as-fed) de "${r.nombre}". Si no deseas definirlo, deja el campo vacío.`,
          buttonText: "Corregir",
        });
        return false;
      }

      if (r.maxKg !== "" && (!Number.isFinite(mx) || mx < 0)) {
        message.alert({
          title: "Máximo inválido",
          text: `Revisa el máximo (kg as-fed) de "${r.nombre}". Si no deseas definirlo, deja el campo vacío.`,
          buttonText: "Corregir",
        });
        return false;
      }

      if (r.minKg !== "" && r.maxKg !== "" && mn > mx) {
        message.alert({
          title: "Rango inválido",
          text: `En "${r.nombre}", el mínimo no puede ser mayor que el máximo.`,
          buttonText: "Corregir",
        });
        return false;
      }
    }
    return true;
  }, [rows, toNum]);

  // ---- Guardar / Continuar ----
  const handleConfirm = () => {
    if (!validar()) return;

    const byId = {};
    const rowsPayload = [];

    for (const r of rows) {
      const costoN = numOr(r.costo, 0);
      const minKgN = r.minKg === "" ? null : toNum(r.minKg);
      const maxKgN = r.maxKg === "" ? null : toNum(r.maxKg);

      const obj = {
        idMateria: r.id,
        nombre: r.nombre,
        tipo: r.temporal ? "Temporal" : "Banco",
        costo: costoN,
        minKg: Number.isFinite(minKgN) ? minKgN : null,
        maxKg: Number.isFinite(maxKgN) ? maxKgN : null,
      };

      byId[r.id] = {
        costo: obj.costo,
        minKg: obj.minKg,
        maxKg: obj.maxKg,
      };
      rowsPayload.push(obj);

      if (typeof DietasController?.setRestriccionMateria === "function") {
        DietasController.setRestriccionMateria(r.id, {
          costo: obj.costo,
          minKg: obj.minKg,
          maxKg: obj.maxKg,
        });
      }
    }

    const payloadPaso4 = { rows: rowsPayload, byId };

    if (typeof DietasController?.setRestricciones === "function") {
      DietasController.setRestricciones(byId);
    }
    localStorage.setItem("np_restr", JSON.stringify(byId));
    localStorage.setItem("np_sel", JSON.stringify(items));
    localStorage.setItem("np_restriccionMateria", JSON.stringify(payloadPaso4));

    message.success("Restricciones guardadas correctamente.", "Hecho");
    onSiguiente?.(payloadPaso4);
  };

  // ---- Render ----
  return (
    <div className="restm-wrap">
      <div className="restm-header-grid">
        <div className="col">Materia prima <span className="req">*</span></div>
        <div className="col">Costo <span className="pill neutral">$/kg</span></div>
        <div className="col">Restricción mínima <span className="pill">kg as-fed</span></div>
        <div className="col">Restricción máxima <span className="pill">kg as-fed</span></div>
      </div>
      <div className="restm-underline" />

      <div className="restm-rows">
        {rows.length === 0 ? (
          <div className="restm-empty">
            No hay materias seleccionadas. Vuelve al paso anterior y confirma al menos una.
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="restm-row">
              <div className="col">
                <div className="restm-name-wrap">
                  <span className="restm-name" title={r.nombre}>{r.nombre}</span>
                  {r.temporal && <span className="pill neutral">Temporal</span>}
                </div>
              </div>

              <div className="col">
                <div className="restm-input-wrap restm-suffix--usd">
                  <input
                    className="restm-input"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={r.costo}
                    onChange={(e) => setField(r.id, "costo", e.target.value)}
                    aria-label={`Costo de ${r.nombre} ($/kg)`}
                  />
                </div>
              </div>

              <div className="col">
                <div className="restm-input-wrap">
                  <input
                    className="restm-input"
                    inputMode="decimal"
                    placeholder="(opcional)"
                    value={r.minKg}
                    onChange={(e) => setField(r.id, "minKg", e.target.value)}
                    aria-label={`Mínimo de ${r.nombre} (kg as-fed)`}
                  />
                </div>
              </div>

              <div className="col">
                <div className="restm-input-wrap">
                  <input
                    className="restm-input"
                    inputMode="decimal"
                    placeholder="(opcional)"
                    value={r.maxKg}
                    onChange={(e) => setField(r.id, "maxKg", e.target.value)}
                    aria-label={`Máximo de ${r.nombre} (kg as-fed)`}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="restm-actions">
        <button type="button" className="btn ghost" onClick={onAtras}>Atrás</button>
        <button type="button" className="btn gold" onClick={handleConfirm}>Confirmar y continuar</button>
      </div>
    </div>
  );
}
